<script lang="ts" setup>
import dayjs from 'dayjs';
import { defaultLocations } from '~/constants/defaultLocations';
import { ONLINE_WAREHOUSE_ID } from '~/constants/warehouse';
import type { OfferBalance } from '~/types/Catalog';
import type { ShopItem } from '~/types/Checkout';
import { getSelectedMulticardOffer } from '~/utils/multicard';

const isShowModal = defineModel<boolean>({ default: false });

const catalogStore = useCatalogStore();
const checkoutStore = useCheckoutStore();
const offerBalances = computed(() => {
  return (
    getSelectedMulticardOffer(catalogStore.selectedOffer, catalogStore.selectedSizeId)?.offer_balances ??
    catalogStore.selectedOffer.offer_balances ??
    []
  );
});

const moskva = defaultLocations[0];

watch(
  () => isShowModal.value,
  () => {
    if (!isShowModal.value) checkoutStore.openMarker = null;
  },
);

const layoutTypes = { map: 'map', list: 'list' };
const selectedBlockLayout = ref(layoutTypes.list);

const shops = ref<ShopItem[]>([]);

async function getShops() {
  shops.value = await checkoutStore.getShopsForProductCard(moskva.data.kladr_id as string);
}

getShops();

function switchLayout() {
  if (selectedBlockLayout.value === layoutTypes.list) {
    selectedBlockLayout.value = layoutTypes.map;
  } else if (selectedBlockLayout.value === layoutTypes.map) {
    selectedBlockLayout.value = layoutTypes.list;
  }
}

const authStore = useAuthStore();

function formatOrderDate(extraDays: number): string {
  const target = dayjs().add(extraDays, 'day');
  const today = dayjs();
  if (target.isSame(today, 'day')) return 'сегодня';
  if (target.isSame(today.add(1, 'day'), 'day')) return 'завтра';
  if (target.isSame(today.add(2, 'day'), 'day')) return 'послезавтра';
  return target.format('D MMMM');
}

function getSourceExtraShippingDays(): number {
  const sources = offerBalances.value.filter(
    (b: OfferBalance) =>
      b.balance > 0 &&
      b.warehouse.show &&
      (b.warehouse.id === ONLINE_WAREHOUSE_ID ||
        (b.warehouse.pickup_store === null && b.warehouse.id !== ONLINE_WAREHOUSE_ID)),
  );
  return sources.length ? Math.min(...sources.map((b) => b.warehouse.extra_shipping_days)) : 0;
}

function getStockInfo(shop: ShopItem) {
  const correspondingWarehouse = offerBalances.value.find((balance) => balance.warehouse.id === shop.warehouse_id);
  if (!correspondingWarehouse) return '';

  const {
    balance,
    warehouse: { pickup_store },
  } = correspondingWarehouse;

  shop.balance = balance ?? 0;

  if (balance < 1) {
    const hasSource = offerBalances.value.some(
      (b: OfferBalance) =>
        b.balance > 0 &&
        b.warehouse.show &&
        (b.warehouse.id === ONLINE_WAREHOUSE_ID ||
          (b.warehouse.pickup_store === null && b.warehouse.id !== ONLINE_WAREHOUSE_ID)),
    );

    if (hasSource && !pickup_store?.only_stock) {
      const orderDate = formatOrderDate(getSourceExtraShippingDays() + 1);
      return `<span class="text-label-violet">Под заказ, ${orderDate}</span>`;
    } else {
      return '<span class="text-black-40">Нет в наличии</span>';
    }
  } else if (balance === 1) {
    return '<span class="text-red">Осталось 1</span>';
  } else if (balance < 5) {
    return `<span class="text-yellow">Осталось ${authStore.accessRights?.can_view_balance ? balance : 'мало'}</span>`;
  } else {
    return `<span class="text-green">Осталось ${authStore.accessRights?.can_view_balance ? balance : 'много'}</span>`;
  }
}

const sortedShops = computed(() => shops.value.sort((a, b) => (b.balance ?? 0) - (a.balance ?? 0)));
</script>

<template>
  <BaseModal
    v-model="isShowModal"
    @close="isShowModal = false"
  >
    <div class="product-page-availability-in-shops-modal">
      <span class="font-bold mt-8 sm:mt-0">Самовывоз из магазинов Wtfish</span>

      <div class="product-page-availability-in-shops-modal__points">
        <section
          :class="{ '!hidden sm:!flex': selectedBlockLayout === layoutTypes.map }"
          class="product-page-availability-in-shops-modal__shops-list"
        >
          <div
            v-for="shop in sortedShops"
            :key="shop.code"
            :class="{
              'product-page-availability-in-shops-modal__shops-list-item--selected':
                checkoutStore.openMarker?.code === shop.code,
            }"
            class="product-page-availability-in-shops-modal__shops-list-item"
            @click="checkoutStore.openMarker = checkoutStore.openMarker?.code === shop.code ? null : shop"
          >
            <div class="text-base font-bold mb-1.5">{{ shop.nearest_station }}</div>
            <div class="font-bold">{{ shop.address }}</div>
            <div>{{ shop.sub_title }}</div>
            <div>{{ shop.work_time_full }}</div>
            <div v-html="getStockInfo(shop)" />
          </div>
        </section>

        <PickupPointsMap
          :class="{ '!hidden sm:!block': selectedBlockLayout === layoutTypes.list }"
          :points="sortedShops"
          :selected-location="moskva"
          class="md:min-h-96"
        />
      </div>

      <TheBtn
        class="mb-3 sm:!hidden"
        @click="switchLayout"
      >
        {{ selectedBlockLayout === layoutTypes.map ? 'Список' : 'Карта' }}
      </TheBtn>
    </div>
  </BaseModal>
</template>

<style lang="scss">
.product-page-availability-in-shops-modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 12px;

  &__points {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    height: 320px;

    @include screen-sm {
      grid-template-columns: 1fr 1fr;
      height: unset;
    }
  }

  &__shops-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
  }

  &__shops-list-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid transparent;
    border-radius: 12px;
    background-color: $black-5;
    font-size: $text-sm;
    cursor: pointer;
    transition: all 0.3s;

    &--selected {
      border: 1px solid $black-60;
    }
  }
}
</style>
